import type { LegalDocumentType } from '@api/hooks/useProfiles';
import { LEGAL_DOCUMENTS } from '@constants/legalDocuments';
import { Loader } from '@gurupay/gp-ui-library';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

const CONTENT_CLASS_NAME = clsx(
	'text-neutral-950',
	'[&_h3]:text-sm-regular [&_h3]:mt-space-16 [&_h3:first-child]:mt-0',
	'[&_p]:text-sm-regular [&_p]:mt-space-8 [&_p:first-child]:mt-0',
	'[&_ul]:list-disc [&_ul]:pl-space-16 [&_ul_ul]:list-[circle] [&_li:has(>ul)]:list-none',
	'[&_li]:text-sm-regular [&_a]:text-brand-secondary-600 [&_a]:underline',
	'[&_table]:border-collapse [&_td_p]:text-xs-regular [&_td_li]:text-xs-regular',
	'[&_td]:border [&_td]:border-neutral-200 [&_td]:p-space-8 [&_td]:text-xs-regular [&_td]:align-top',
);

interface LegalDocumentPanelProps {
	heightClassName?: string;
	type: LegalDocumentType;
}

export const LegalDocumentPanel = ({
	heightClassName = 'h-62.5 min-mobile:h-[min(420px,45dvh)]',
	type,
}: LegalDocumentPanelProps) => {
	const { namespace } = LEGAL_DOCUMENTS[type];
	const { ready, t } = useTranslation(namespace, { useSuspense: false });

	return (
		<div
			aria-label={ready ? t('title') : undefined}
			className={clsx(
				heightClassName,
				'w-full overflow-y-auto pt-space-16 pr-space-12 pb-space-16 pl-space-16 border border-neutral-200 rounded-lg bg-neutral-white text-neutral-950',
			)}
			role="tabpanel"
			// eslint-disable-next-line jsx-a11y-x/no-noninteractive-tabindex
			tabIndex={0}
		>
			{!ready ? (
				<div className="flex h-full items-center justify-center">
					<Loader />
				</div>
			) : (
				<div className="space-y-space-16">
					<h3 className="text-base-bold">{t('title')}</h3>
					{/* TODO: sanitize HTML with DOMPurify before rendering */}
					<div
						className={CONTENT_CLASS_NAME}
						dangerouslySetInnerHTML={{ __html: t('content') }}
					/>
				</div>
			)}
		</div>
	);
};
